import {
  DEFAULT_IS_WITH_TAX,
  DEFAULT_PERIOD_INTERVAL,
  DEFAULT_PERIOD_OPTIONS,
  DEFAULT_PERIOD_PERIOD,
  DEFAULT_PERIOD_REFERENTIAL_INTERVALS,
  DEFAULT_TAXONOMY,
  FILTERS_STATE_DEFAULT_VALUES
} from '#composables/kpisPerimeter';
import { isTRPCClientError } from '#composables/useTrpc';
import { useImpersonateToken } from '#domain/core/admin/composables/useImpersonateToken';
import {
  getCarrefourPeriodInterval,
  getCurrentDateReferentialInterval
} from '#domain/core/filter/components/periodSelector/periodSelectorUtils';
import { getMe, type UserType } from '#domain/core/user/user.query';
import { isApiErrorOfType } from '#helpers';
import {
  ApiErrorCode,
  DEFAULT_CARREFOUR_REFERENTIAL_INTERVAL,
  isUserInternal,
  type AppScopeKpisPerimeterType,
  type AvailableLanguagesType,
  type DatePeriodIntervalType,
  type DefaultFiltersType,
  type FeaturePermission,
  type FiltersStateType,
  type NielsenPeriodIntervalType,
  type ProductTaxonomyType,
  type ReferentialIntervalsType,
  type StorePerimeterType,
  type UserScope,
  type UserWithScope
} from '@carrefour-gcs/shared';
import { defineStore } from 'pinia';

interface UserStoreStateType {
  authenticatedUser: UserType | null;
  defaultKpisPerimeter: {
    taxonomy: ProductTaxonomyType;
    isWithTax: boolean;
    filters: FiltersStateType;
    period: DatePeriodIntervalType | NielsenPeriodIntervalType;
    referentialIntervals: ReferentialIntervalsType;
    storePerimeter?: StorePerimeterType;
  };
}
export const useUserStore = defineStore('user', {
  state: (): UserStoreStateType => ({
    authenticatedUser: null,
    defaultKpisPerimeter: {
      taxonomy: DEFAULT_TAXONOMY,
      isWithTax: DEFAULT_IS_WITH_TAX,
      filters: FILTERS_STATE_DEFAULT_VALUES,
      period: {
        periodInterval: DEFAULT_PERIOD_INTERVAL,
        period: DEFAULT_PERIOD_PERIOD,
        option: DEFAULT_PERIOD_OPTIONS
      },
      referentialIntervals: DEFAULT_PERIOD_REFERENTIAL_INTERVALS,
      storePerimeter: undefined
    }
  }),
  actions: {
    async fetchAuthenticatedUser() {
      const impersonateToken = useImpersonateToken();
      let user: UserWithScope;

      try {
        user = await getMe();
      } catch (error) {
        if (
          impersonateToken.value &&
          isTRPCClientError(error) &&
          isApiErrorOfType(error.shape, ApiErrorCode.AUTHENT_UNAUTHENTICATED)
        ) {
          impersonateToken.value = null;
          window.location.reload();
        }

        // Rethrow original tRPC error so `authStore.initAuth` can detect the 401 status
        // and redirect directly to /login, avoiding a flash of the "no access" error page.
        throw error;
      }

      this.authenticatedUser = user;

      if (user.appScope) {
        this.setDefaultUserKpisPerimeter(user.appScope);
      }

      return user;
    },
    setDefaultUserKpisPerimeter(userAppScope: AppScopeKpisPerimeterType) {
      const filters = getFiltersFromUserScope(userAppScope.defaultFilters);
      const { defaultPeriod } = getDefaultCarrefourPeriodsFromUserScope(userAppScope);

      this.defaultKpisPerimeter.isWithTax = userAppScope.isWithTax ?? false;

      this.defaultKpisPerimeter.filters = filters;

      this.defaultKpisPerimeter.period = defaultPeriod;
      this.defaultKpisPerimeter.referentialIntervals = userAppScope.referentialIntervals;

      this.defaultKpisPerimeter.storePerimeter = userAppScope.storePerimeter;
    },
    updateDefaultLanguage(language: AvailableLanguagesType) {
      if (this.authenticatedUser?.appScope) {
        this.authenticatedUser.appScope.lang = language;
      }
    }
  },
  getters: {
    isUserOnboarding: (state) => {
      return (
        state.authenticatedUser &&
        !state.authenticatedUser.scope?.flags.isAdmin &&
        !state.authenticatedUser.appScope?.validated
      );
    },
    userCountry: (state) => {
      return state.authenticatedUser?.scope?.organization.country;
    },
    isAdmin: (state) => {
      return !!state.authenticatedUser?.scope?.flags.isAdmin;
    },
    isAccountManager: (state) => {
      return !!state.authenticatedUser?.scope?.flags.isAccountManager;
    },
    isOrganizationsCreator: (state) => {
      return !!state.authenticatedUser?.scope?.flags.isOrganizationsCreator;
    },
    isAnonymized: (state) => {
      return !!state.authenticatedUser?.scope?.flags.isAnonymized;
    },
    isProjectsAuthorized: (state) => {
      return state.authenticatedUser?.scope?.permissions.features.some((feature: FeaturePermission) =>
        ['projects'].includes(feature)
      );
    },
    isInnovationsAuthorized: (state) => {
      return state.authenticatedUser?.scope?.permissions.features.includes('innovationSupplier');
    },
    isImpersonating: (state) => {
      return !!state.authenticatedUser?.isImpersonating;
    },
    scope: (state): UserScope | null => {
      if (!state.authenticatedUser?.scope) {
        return null;
      }

      return state.authenticatedUser.scope;
    },
    isInternal: (state): boolean => {
      return state.authenticatedUser?.scope ? isUserInternal(state.authenticatedUser.scope) : false;
    }
  }
});

function getDefaultCarrefourPeriodsFromUserScope(userScope: AppScopeKpisPerimeterType) {
  const intervalLastPeriod =
    userScope.referentialIntervals.carrefour.standard[userScope.selectedPeriod?.period || DEFAULT_PERIOD_PERIOD];
  const currentDateReferentialInterval = getCurrentDateReferentialInterval({
    currentTaxonomy: 'carrefour',
    referentialIntervals: userScope.referentialIntervals,
    selectedPeriodType: userScope.selectedPeriod?.period || DEFAULT_PERIOD_PERIOD,
    carrefourReferentialIntervals: DEFAULT_CARREFOUR_REFERENTIAL_INTERVAL
  });
  const interval = getCarrefourPeriodInterval({
    selectedPeriod: userScope.selectedPeriod?.period || DEFAULT_PERIOD_PERIOD,
    currentDateReferentialInterval,
    selectedInterval: intervalLastPeriod,
    selectedCalendarOption: userScope.selectedPeriod?.option || DEFAULT_PERIOD_OPTIONS
  });

  const selectedPeriod = {
    periodInterval: interval,
    period: userScope.selectedPeriod?.period || DEFAULT_PERIOD_PERIOD,
    option: userScope.selectedPeriod?.option || DEFAULT_PERIOD_OPTIONS
  };

  return {
    defaultPeriod: { ...selectedPeriod }
  };
}

function getFiltersFromUserScope(defaultFilters?: DefaultFiltersType): FiltersStateType {
  return {
    nomenclatureFilters:
      defaultFilters?.nomenclatureFilters.map(({ nodeId }) => nodeId) ??
      FILTERS_STATE_DEFAULT_VALUES['nomenclatureFilters'],
    activityTypesFilters:
      defaultFilters?.activityTypesFilters.map(({ nodeId }) => nodeId) ??
      FILTERS_STATE_DEFAULT_VALUES['activityTypesFilters'],
    storesFilters:
      defaultFilters?.storesFilters.map(({ nodeId }) => nodeId) ?? FILTERS_STATE_DEFAULT_VALUES['storesFilters'],
    barcodeFilters: FILTERS_STATE_DEFAULT_VALUES['barcodeFilters'],
    suppliersFilters: FILTERS_STATE_DEFAULT_VALUES['suppliersFilters'],
    bemHoldingFilters: FILTERS_STATE_DEFAULT_VALUES['bemHoldingFilters'],
    bemMerchandiseFilters: FILTERS_STATE_DEFAULT_VALUES['bemMerchandiseFilters'],
    brandsFilters: FILTERS_STATE_DEFAULT_VALUES['brandsFilters'],
    integratedFranchisedFilters: FILTERS_STATE_DEFAULT_VALUES['integratedFranchisedFilters'],
    itemFilters: FILTERS_STATE_DEFAULT_VALUES['itemFilters'],
    triptyqueFilters: FILTERS_STATE_DEFAULT_VALUES['triptyqueFilters'],
    storesRegionFilters:
      defaultFilters?.storesRegionFilters.map(({ nodeId }) => nodeId) ??
      FILTERS_STATE_DEFAULT_VALUES['storesRegionFilters'],
    assortmentTypologyFilters: FILTERS_STATE_DEFAULT_VALUES['assortmentTypologyFilters'],
    productTypologyFilters: FILTERS_STATE_DEFAULT_VALUES['productTypologyFilters'],
    competitorFilters: FILTERS_STATE_DEFAULT_VALUES['competitorFilters'],
    competitorBaseBarcodeFilters: FILTERS_STATE_DEFAULT_VALUES['competitorBaseBarcodeFilters'],
    competitorBaseItemFilters: FILTERS_STATE_DEFAULT_VALUES['competitorBaseItemFilters'],
    competitorComparisonBarcodeFilters: FILTERS_STATE_DEFAULT_VALUES['competitorComparisonBarcodeFilters'],
    competitorComparisonItemFilters: FILTERS_STATE_DEFAULT_VALUES['competitorComparisonItemFilters'],
    competitorBrandsFilters: FILTERS_STATE_DEFAULT_VALUES['competitorBrandsFilters'],
    consostyleMicroFilters: FILTERS_STATE_DEFAULT_VALUES['consostyleMicroFilters'],
    lifestageMicroFilters: FILTERS_STATE_DEFAULT_VALUES['lifestageMicroFilters'],
    businessBrandFilters: FILTERS_STATE_DEFAULT_VALUES['businessBrandFilters'],
    businessBrandReducedFilters: FILTERS_STATE_DEFAULT_VALUES['businessBrandReducedFilters']
  };
}
